import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["productRow", "selectionInput", "quantityWrapper", "totalDisplay", "totalValue", "remainingValue", "extraDepositRow", "extraDepositValue", "validationInput", "errorMessage", "capacityLabel"]
  
  static values = {
    selectionMode: { type: String, default: 'single' },
    isEarlyPrice: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    depositCents: { type: Number, default: 0 }
  }

  connect() {
    this.calculateTotal()
    this.updateValidation()
    this.updateCapacityLabels()
    
    // Add form submit listener for validation
    this.form = this.element.closest('form')
    if (this.form && this.requiredValue) {
      this.boundValidate = this.validateOnSubmit.bind(this)
      this.form.addEventListener('submit', this.boundValidate)
    }
  }

  disconnect() {
    if (this.form && this.boundValidate) {
      this.form.removeEventListener('submit', this.boundValidate)
    }
  }

  validateOnSubmit(event) {
    const selectableRows = this.productRowTargets.filter(row => row.dataset.soldOut !== 'true')
    const allSoldOut = selectableRows.length === 0
    const hasSelection = selectableRows.some(row => row.classList.contains('selected'))
    
    if (!hasSelection && !allSoldOut) {
      event.preventDefault()
      this.showError()
      this.element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return false
    }
    
    this.hideError()
    return true
  }

  showError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.style.display = 'block'
    }
    this.element.classList.add('has-error')
  }

  hideError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.style.display = 'none'
    }
    this.element.classList.remove('has-error')
  }

  onSelectionChange(event) {
    const input = event.target
    const row = input.closest('.product-row')

    // Prevent interaction with sold-out rows
    if (row.dataset.soldOut === 'true') {
      input.checked = false
      return
    }

    const isChecked = input.checked
    
    if (this.selectionModeValue === 'single') {
      // For radio buttons, unselect all other rows
      this.productRowTargets.forEach(r => {
        r.classList.remove('selected')
        const qtyWrapper = r.querySelector('[data-payment-product-selection-target="quantityWrapper"]')
        if (qtyWrapper) {
          qtyWrapper.style.visibility = 'hidden'
        }
      })
    }
    
    if (isChecked) {
      row.classList.add('selected')
      const qtyWrapper = row.querySelector('[data-payment-product-selection-target="quantityWrapper"]')
      if (qtyWrapper) {
        qtyWrapper.style.visibility = 'visible'
      }
    } else {
      row.classList.remove('selected')
      const qtyWrapper = row.querySelector('[data-payment-product-selection-target="quantityWrapper"]')
      if (qtyWrapper) {
        qtyWrapper.style.visibility = 'hidden'
      }
    }
    
    this.calculateTotal()
    this.updateValidation()
    this.updateCapacityLabels()
    this.hideError()
  }

  onQuantityChange(event) {
    this.calculateTotal()
    this.updateCapacityLabels()
  }

  updateValidation() {
    if (!this.hasValidationInputTarget) return
    
    const hasSelection = this.productRowTargets
      .filter(row => row.dataset.soldOut !== 'true')
      .some(row => row.classList.contains('selected'))
    this.validationInputTarget.value = hasSelection ? '1' : ''
  }

  updateCapacityLabels() {
    this.productRowTargets.forEach(row => {
      const label = row.querySelector('[data-payment-product-selection-target="capacityLabel"]')
      if (!label) return
      if (row.dataset.showCapacity !== 'true' || row.dataset.soldOut === 'true') {
        label.hidden = true
        return
      }
      const remainingRaw = parseInt(row.dataset.capacityRemaining, 10)
      if (Number.isNaN(remainingRaw)) {
        label.hidden = true
        return
      }
      const configuredMax = parseInt(row.dataset.configuredMaxQty, 10)
      const isSelected = row.classList.contains('selected')
      let qty = 0
      if (isSelected) {
        const qtyInput = row.querySelector('[data-quantity-selector-target="input"]')
        qty = qtyInput ? parseInt(qtyInput.value, 10) || 0 : 0
      }
      const liveRemaining = Math.max(remainingRaw - qty, 0)
      const threshold = Number.isFinite(configuredMax) ? configuredMax : remainingRaw
      if (liveRemaining < threshold) {
        label.textContent = `${liveRemaining} left`
        label.hidden = false
      } else {
        label.hidden = true
      }
    })
  }

  calculateTotal() {
    let total = 0
    
    this.productRowTargets.forEach(row => {
      if (row.classList.contains('selected') && row.dataset.soldOut !== 'true') {
        const earlyPrice = parseFloat(row.dataset.earlyPrice) || 0
        const regularPrice = parseFloat(row.dataset.regularPrice) || 0
        const price = this.isEarlyPriceValue ? earlyPrice : regularPrice
        
        const quantityInput = row.querySelector('[data-quantity-selector-target="input"]')
        const quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1
        
        total += price * quantity
      }
    })
    
    if (this.hasTotalValueTarget) {
      this.totalValueTarget.textContent = this.formatPrice(total)
    }

    const deposit = this.depositCentsValue / 100
    const remaining = Math.max(total - deposit, 0)
    const extraDeposit = Math.max(deposit - total, 0)

    if (this.hasRemainingValueTarget) {
      this.remainingValueTarget.textContent = this.formatPrice(remaining)
    }

    if (this.hasExtraDepositRowTarget && this.hasExtraDepositValueTarget) {
      this.extraDepositValueTarget.textContent = this.formatPrice(extraDeposit)
      this.extraDepositRowTarget.classList.toggle('d-none', extraDeposit <= 0)
    }
  }

  formatPrice(amount) {
    if (isNaN(amount) || amount === null || amount === undefined || amount === '') amount = 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    }).format(amount);
  }
}
